import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import {
  Copy, Download, Check, Share2, Instagram, Facebook, Youtube, MessageCircle,
  Mail, ArrowLeft, QrCode as QrIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function TikTokIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.16 8.16 0 0 0 4.77 1.52V6.31a4.85 4.85 0 0 1-1.84-.36 5 5 0 0 1 0 .74Z"/>
    </svg>
  );
}

const DEFAULT_CAPTION = `🏁 Session simulateur pro chez KV International — Rhode-Saint-Genèse.
Programme sur mesure · Feedback pilote temps réel · Analyse télémétrique.
Réservez la vôtre 👉 kvinternational.be/simulator

#KVSimStage #KVInternational #Porsche #DrivingSimulator #Belgium`;

const HASHTAGS = "#KVSimStage #KVInternational #Porsche #DrivingSimulator #Motorsport #Belgium #LaHulpe #Sportcars";

export default function Share() {
  const { t } = useLang();
  const [copied, setCopied] = useState({ url: false, caption: false, tags: false });
  const [caption, setCaption] = useState(DEFAULT_CAPTION);
  const [site, setSite] = useState({});

  useEffect(() => { api.get("/site").then((r) => setSite(r.data)).catch(() => {}); }, []);

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "";
  const simUrl = `${siteUrl}/simulator`;
  const qrData = simUrl;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=20&bgcolor=0D0D0D&color=C8A75A&data=${encodeURIComponent(qrData)}`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((c) => ({ ...c, [key]: true }));
      toast.success("Copié");
      setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1600);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const shareTargets = [
    { label: "Instagram", icon: Instagram, color: "#E1306C", href: site.instagram || "https://www.instagram.com/", note: "Postez photo + réutilisez la légende" },
    { label: "TikTok", icon: TikTokIcon, color: "#25F4EE", href: site.tiktok || "https://www.tiktok.com/upload", note: "Reel 9:16 · musique dynamique" },
    { label: "Facebook", icon: Facebook, color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(simUrl)}`, note: "Partagez direct sur votre mur" },
    { label: "YouTube Shorts", icon: Youtube, color: "#FF0000", href: site.youtube || "https://studio.youtube.com/", note: "Upload en Short 9:16 · <60s" },
    { label: "WhatsApp", icon: MessageCircle, color: "#25D366", href: `https://wa.me/?text=${encodeURIComponent(caption + "\n\n" + simUrl)}`, note: "Envoyez à un contact ou statut" },
    { label: "Email", icon: Mail, color: "#C8A75A", href: `mailto:?subject=${encodeURIComponent("Stage simulateur KV International")}&body=${encodeURIComponent(caption + "\n\n" + simUrl)}`, note: "Invitation par email" },
  ];

  return (
    <div data-testid="share-page" className="pt-32 pb-24 bg-[#0D0D0D] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Link to="/simulator" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C8A75A] mb-8">
          <ArrowLeft className="w-3 h-3" /> Retour au simulateur
        </Link>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Kit de partage</p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light text-white">Partagez KV en un scan.</h1>
            <p className="text-white/60 text-lg mt-4 max-w-2xl">
              QR code officiel, légende toute prête, hashtags et boutons de partage direct pour Instagram, TikTok, Facebook, WhatsApp & plus.
            </p>

            {/* Caption editor */}
            <div className="mt-12 bg-[#1E1E1E] border border-white/10 rounded-md p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">Légende à copier</p>
                <button
                  data-testid="copy-caption"
                  onClick={() => copy(caption, "caption")}
                  className="text-xs uppercase tracking-widest text-[#C8A75A] hover:text-white flex items-center gap-2"
                >
                  {copied.caption ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied.caption ? "Copié" : "Copier"}
                </button>
              </div>
              <Textarea
                data-testid="caption-textarea"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={6}
                className="bg-[#0D0D0D] border-white/10 text-white text-sm leading-relaxed"
              />
            </div>

            {/* URL + hashtags */}
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <QuickCopyRow label="URL simulateur" value={simUrl} onCopy={() => copy(simUrl, "url")} copied={copied.url} testid="copy-url" />
              <QuickCopyRow label="Hashtags" value={HASHTAGS} onCopy={() => copy(HASHTAGS, "tags")} copied={copied.tags} testid="copy-tags" />
            </div>

            {/* Share targets grid */}
            <div className="mt-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Publier directement</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {shareTargets.map((s, i) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`share-${s.label.toLowerCase()}`}
                    className="group bg-[#1E1E1E] border border-white/10 hover:border-[#C8A75A] rounded-md p-5 transition-all duration-300 hover:-translate-y-1 flex items-center gap-4"
                  >
                    <span
                      className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `${s.color}20`, color: s.color }}
                    >
                      <s.icon className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-white group-hover:text-[#C8A75A] transition-colors">{s.label}</p>
                      <p className="text-xs text-white/50 mt-0.5">{s.note}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* QR Code panel */}
          <div className="lg:sticky lg:top-28">
            <div className="bg-[#1E1E1E] border border-[#C8A75A]/30 rounded-md p-8 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
                background: "radial-gradient(circle at center, rgba(200,167,90,0.25), transparent 60%)"
              }} />
              <div className="relative z-10">
                <div className="flex items-center justify-center mb-5">
                  <Logo asLink={false} withText={false} size="w-14 h-14" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#C8A75A] mb-2">Scan · Reserve · Ride</p>
                <p className="font-display text-2xl text-white mb-6">QR officiel simulateur</p>

                <div className="relative bg-[#0D0D0D] p-3 rounded-md inline-block mx-auto border border-[#C8A75A]/20 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                  <img
                    src={qrUrl}
                    alt="QR simulateur"
                    data-testid="qr-code-image"
                    className="w-64 h-64"
                  />
                  <span className="absolute -top-3 -right-3 bg-[#C8A75A] text-black text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm">Live</span>
                </div>

                <p className="text-white/50 text-xs mt-6 break-all">{qrData}</p>

                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <a
                    href={qrUrl}
                    download="kv-international-qr.png"
                    data-testid="qr-download"
                    className="bg-[#C8A75A] text-black hover:bg-white transition-colors text-xs uppercase tracking-widest px-6 py-3 rounded-md inline-flex items-center gap-2 font-medium"
                  >
                    <Download className="w-4 h-4" /> Télécharger
                  </a>
                  <button
                    data-testid="qr-copy-link"
                    onClick={() => copy(qrData, "url")}
                    className="border border-white/20 text-white hover:bg-white/5 transition-colors text-xs uppercase tracking-widest px-6 py-3 rounded-md inline-flex items-center gap-2"
                  >
                    <QrIcon className="w-4 h-4" /> Copier le lien
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 bg-[#1E1E1E]/60 border border-white/10 rounded-md p-6 text-sm text-white/60 leading-relaxed">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A] mb-2">Conseil</p>
              Imprimez ce QR au format A5 et posez-le à côté du simulateur, dans le showroom ou sur les fiches véhicules. Chaque scan mène directement à la page de réservation.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickCopyRow({ label, value, onCopy, copied, testid }) {
  return (
    <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-4">
      <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">{label}</p>
      <div className="flex items-center gap-2">
        <Input value={value} readOnly className="bg-[#0D0D0D] border-white/10 text-white text-sm h-10 truncate" />
        <Button data-testid={testid} onClick={onCopy} size="sm" className="bg-[#C8A75A] text-black hover:bg-white h-10 flex-shrink-0">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
